import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { useSEO } from '../hooks/useSEO';

const PageContainer = styled.div`
  min-height: 80vh;
  padding: var(--spacing-8) 0;
`;

const Hero = styled.section`
  position: relative;
  overflow: hidden;
  text-align: center;
  padding: clamp(var(--spacing-10), 6vw, var(--spacing-14)) 0;
  margin-bottom: var(--spacing-12);
  background: radial-gradient(900px 400px at 15% 0%, #fef7ff 0%, rgba(254,247,255,0.6) 60%, transparent 100%),
              radial-gradient(900px 400px at 85% 100%, #ecfdf5 0%, rgba(236,253,245,0.6) 60%, transparent 100%),
              linear-gradient(135deg, #f8fafc 0%, #f5f3ff 100%);
  border-radius: var(--border-radius-2xl);
  box-shadow: var(--shadow-lg);

  h1 {
    color: var(--color-secondary);
    font-size: var(--font-size-4xl);
    font-weight: 700;
    margin-bottom: var(--spacing-3);
  }

  p {
    max-width: 760px;
    margin: var(--spacing-4) auto var(--spacing-6);
    color: var(--color-text);
    font-size: var(--font-size-lg);
    opacity: 0.95;
  }
`;

const FAQGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: clamp(var(--spacing-4), 3vw, var(--spacing-8));
`;

const Section = styled.section`
  background: linear-gradient(180deg, #ffffff 0%, #fafafa 100%);
  border: 1px solid rgba(2,6,23,0.06);
  border-radius: var(--border-radius-2xl);
  box-shadow: 0 10px 24px rgba(2,6,23,0.06);
  padding: clamp(var(--spacing-6), 3vw, var(--spacing-8));
`;

const SectionTitle = styled.h2`
  color: var(--color-secondary);
  font-size: var(--font-size-2xl);
  font-weight: 700;
  margin-bottom: var(--spacing-4);
`;

const QA = styled.details`
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-xl);
  padding: var(--spacing-3) var(--spacing-4);
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
  box-shadow: var(--shadow-sm);
  margin-bottom: var(--spacing-3);

  &[open] {
    box-shadow: var(--shadow);
    border-color: rgba(79,70,229,0.25);
  }

  summary {
    cursor: pointer;
    list-style: none;
    font-weight: 600;
    color: var(--color-secondary);
    outline: none;
  }
  summary::-webkit-details-marker { display: none; }

  .answer {
    margin-top: var(--spacing-2);
    color: var(--color-text);
    line-height: 1.7;
  }
`;

const faqStructuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Hur gör jag en beställning?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Lägg önskade produkter i varukorgen och gå vidare till kassan för att slutföra ditt köp."
      }
    },
    {
      "@type": "Question",
      "name": "Kan jag ändra eller avbryta min order?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Kontakta oss så snart som möjligt. Har ordern inte skickats kan vi vanligtvis hjälpa till att ändra eller avbryta."
      }
    },
    {
      "@type": "Question",
      "name": "Jag fick ingen orderbekräftelse?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Kolla skräpposten eller kontakta oss så skickar vi bekräftelsen igen."
      }
    },
    {
      "@type": "Question",
      "name": "Hur lång är leveranstiden?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Normalt 1–3 arbetsdagar. Under kampanjer kan det ta lite längre tid."
      }
    },
    {
      "@type": "Question",
      "name": "Vad kostar frakten?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "59 kr. Fri frakt över 500 kr."
      }
    },
    {
      "@type": "Question",
      "name": "Hur returnerar jag en produkt?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Kontakta oss med ordernummer så skickar vi en returetikett. Returen ska vara i oanvänt skick och i originalförpackning."
      }
    },
    {
      "@type": "Question",
      "name": "Hur lång tid tar återbetalningen?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "3–7 bankdagar efter att vi mottagit och godkänt returen."
      }
    },
    {
      "@type": "Question",
      "name": "Finns det undantag?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Specialbeställningar och förseglade hygienprodukter (med bruten försegling) omfattas inte av ångerrätten."
      }
    },
    {
      "@type": "Question",
      "name": "Vilka betalningsmetoder erbjuder ni?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Kort, Swish och faktura via våra betalpartners."
      }
    },
    {
      "@type": "Question",
      "name": "Är min betalning säker?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Ja. Alla betalningar hanteras krypterat av certifierade betalningsleverantörer."
      }
    },
    {
      "@type": "Question",
      "name": "Är färger och material exakt som på bild?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Små variationer kan förekomma beroende på skärm och tillverkning. Vi försöker beskriva produkterna så tydligt som möjligt."
      }
    },
    {
      "@type": "Question",
      "name": "Finns varan i lager?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Lagersaldo visas på produktsidan. Kontakta oss om du undrar över större kvantiteter."
      }
    },
    {
      "@type": "Question",
      "name": "Kan ni slå in produkterna som present?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Ja, vi erbjuder presentinslagning på utvalda varor. Lägg till en notering i kassan eller kontakta oss via kontakt så hjälper vi dig med en omsorgsfull presentförpackning."
      }
    },
    {
      "@type": "Question",
      "name": "Var hittar jag guider och inspiration?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Titta in i Floristbloggen eller våra specialguider som Julpynt och andra marketing-sidor med stylingtips, presentidéer och utförliga guider."
      }
    },
    {
      "@type": "Question",
      "name": "Hur kontaktar jag kundservice?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Mejla oss på butik(at)lillaflora.nu eller ring 0708555052. Vi svarar vardagar 09‑17."
      }
    },
    {
      "@type": "Question",
      "name": "Har ni butik?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Ja, besök oss gärna i Kramfors. Se adress i sidfoten."
      }
    }
  ]
};

const FAQ: React.FC = () => {
  useSEO({
    entityType: 'page',
    entityId: 'faq',
    fallbackTitle: 'Vanliga frågor – Lillaflora',
    fallbackDescription: 'Svar på vanliga frågor om beställning, leverans, retur och kontakt med vår blomsterbutik i Kramfors.',
    fallbackImage: '/static/images/lillaflora-hero.jpg'
  });
  return (
    <PageContainer>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqStructuredData)}</script>
      </Helmet>
      <div className="container">
        <Hero>
          <h1>Vanliga frågor</h1>
          <p>Hittar du inte svaret? Kontakta oss – vi hjälper gärna till.</p>
        </Hero>

        <FAQGrid>
          <Section>
            <SectionTitle>Beställning</SectionTitle>
            <QA>
              <summary>Hur gör jag en beställning?</summary>
              <div className="answer">Lägg önskade produkter i varukorgen och gå vidare till kassan för att slutföra ditt köp.</div>
            </QA>
            <QA>
              <summary>Kan jag ändra eller avbryta min order?</summary>
              <div className="answer">Kontakta oss så snart som möjligt. Har ordern inte skickats kan vi vanligtvis hjälpa till att ändra eller avbryta.</div>
            </QA>
            <QA>
              <summary>Jag fick ingen orderbekräftelse?</summary>
              <div className="answer">Kolla skräpposten eller kontakta oss så skickar vi bekräftelsen igen.</div>
            </QA>
          </Section>

          <Section>
            <SectionTitle>Leverans</SectionTitle>
            <QA>
              <summary>Hur lång är leveranstiden?</summary>
              <div className="answer">Normalt 1–3 arbetsdagar. Under kampanjer kan det ta lite längre tid.</div>
            </QA>
            <QA>
              <summary>Vad kostar frakten?</summary>
              <div className="answer">59 kr. Fri frakt över 500 kr.</div>
            </QA>
          </Section>

          <Section>
            <SectionTitle>Retur & Byte</SectionTitle>
            <QA>
              <summary>Hur returnerar jag en produkt?</summary>
              <div className="answer">Kontakta oss med ordernummer så skickar vi en returetikett. Returen ska vara i oanvänt skick och i originalförpackning.</div>
            </QA>
            <QA>
              <summary>Hur lång tid tar återbetalningen?</summary>
              <div className="answer">3–7 bankdagar efter att vi mottagit och godkänt returen.</div>
            </QA>
            <QA>
              <summary>Finns det undantag?</summary>
              <div className="answer">Specialbeställningar och förseglade hygienprodukter (med bruten försegling) omfattas inte av ångerrätten.</div>
            </QA>
          </Section>

          <Section>
            <SectionTitle>Betalning</SectionTitle>
            <QA>
              <summary>Vilka betalningsmetoder erbjuder ni?</summary>
              <div className="answer">Kort, Swish och faktura via våra betalpartners.</div>
            </QA>
            <QA>
              <summary>Är min betalning säker?</summary>
              <div className="answer">Ja. Alla betalningar hanteras krypterat av certifierade betalningsleverantörer.</div>
            </QA>
          </Section>

          <Section>
          <SectionTitle>Produkter</SectionTitle>
          <QA>
            <summary>Är färger och material exakt som på bild?</summary>
            <div className="answer">Små variationer kan förekomma beroende på skärm och tillverkning. Vi försöker beskriva produkterna så tydligt som möjligt.</div>
          </QA>
          <QA>
            <summary>Finns varan i lager?</summary>
            <div className="answer">Lagersaldo visas på produktsidan. Kontakta oss om du undrar över större kvantiteter.</div>
          </QA>
          <QA>
            <summary>Kan ni slå in produkterna som present?</summary>
            <div className="answer">
              Ja, vi erbjuder presentinslagning på utvalda varor. Lägg till en notering i kassan eller kontakta oss via{' '}
              <Link to="/kontakt">kontakt</Link> så hjälper vi dig med en omsorgsfull presentförpackning.
            </div>
          </QA>
          <QA>
            <summary>Var hittar jag guider och inspiration?</summary>
            <div className="answer">
              Titta in i <Link to="/floristbloggen">Floristbloggen</Link> eller våra specialguider som <Link to="/julpynt">Julpynt</Link> och andra marketing-sidor med stylingtips, presentidéer och utförliga guider.
            </div>
          </QA>
        </Section>

          <Section>
            <SectionTitle>Kontakt</SectionTitle>
            <QA>
              <summary>Hur kontaktar jag kundservice?</summary>
              <div className="answer">Mejla oss på butik(at)lillaflora.nu eller ring 0708555052. Vi svarar vardagar 09‑17.</div>
            </QA>
            <QA>
              <summary>Har ni butik?</summary>
              <div className="answer">Ja, besök oss gärna i Kramfors. Se adress i sidfoten.</div>
            </QA>
          </Section>
        </FAQGrid>
      </div>
    </PageContainer>
  );
};

export default FAQ;
